import { useState, useCallback, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { blobToBase64 } from '../../lib/utils.js'
import type { VoiceContextUpdate } from '../../types/voice.js'
import { LiveConnectionState } from '../../types/visual.js'
import { MEDIA_CAPTURE_CONFIG, RATE_LIMITS } from '../../config/constants.js'
import { logger } from '../../lib/logger-client.js'
import type { LiveMediaFrame } from '../../live/liveTypes.js'
import {
  captureVideoFrame,
  createMetrics,
  recordCaptureTime,
  getMetricsSnapshot,
  stopStreamTracks,
  registerVisibilityCleanup,
  maybeAnalyzeAndBroadcast,
  waitForVideoReady,
  type CaptureMetrics,
} from './capture-utils.js'
import { CaptureLifecycle } from './capture-lifecycle.js'
import { mediaLocaleFrom, screenShareFailure } from '../../lib/media-capture-message.js'

export interface UseScreenShareOptions {
  onCapture?: (blob: Blob, imageData?: string) => void
  onAnalysis?: (analysis: string, imageData: string, capturedAt: number) => void
  onFirstFrameSent?: (elapsedMs: number) => void
  captureInterval?: number
  enableAutoCapture?: boolean
  maxDimension?: number
  quality?: number
  sessionId?: string
  voiceConnectionId?: string
  requireVoiceSession?: boolean
  sendRealtimeInput?: (chunks: Array<{ mimeType: string; data: string }>) => void
  sendVideoFrame?: (frame: LiveMediaFrame) => boolean | void
  sendContextUpdate?: (update: VoiceContextUpdate) => void
  analyzeVisualDelta?: (currentAnalysis: string, previousAnalysis?: string) => {
    significant: boolean
    score: number
  }
  /** Called before getDisplayMedia to pause WS heartbeat during browser dialog */
  onBeforeMediaDialog?: () => void
  /** Called after getDisplayMedia resolves/rejects to resume WS heartbeat */
  onAfterMediaDialog?: () => void
  /** Voice/WS connection state — used to handle reconnect for active screenshare */
  connectionState?: LiveConnectionState
}

export interface ScreenShareCapture {
  blob: Blob
  imageData?: string
  timestamp: number
  metadata?: {
    width: number
    height: number
  }
}

// CaptureMetrics imported from capture-utils (shared with useCamera)

type ExtendedCaptureMetrics = CaptureMetrics & { lastAnalysis?: string }

function detachScreenShareEndedHandlers(stream: MediaStream): void {
  stream.getTracks().forEach(track => {
    const typedTrack = track as MediaStreamTrack & { _fbcEndedHandler?: () => void }
    if (typedTrack._fbcEndedHandler) {
      track.removeEventListener('ended', typedTrack._fbcEndedHandler)
      delete typedTrack._fbcEndedHandler
    }
  })
}

function stopScreenShareStream(stream: MediaStream): void {
  detachScreenShareEndedHandlers(stream)
  stopStreamTracks(stream)
}

export function useScreenShare(options: UseScreenShareOptions = {}) {
  const {
    onCapture,
    onAnalysis,
    onFirstFrameSent,
    captureInterval = 4000,
    enableAutoCapture = false,
    maxDimension = 1280,
    quality = 0.7,
    sessionId,
    voiceConnectionId,
    requireVoiceSession = false,
    sendRealtimeInput,
    sendVideoFrame,
    sendContextUpdate,
    analyzeVisualDelta,
    onBeforeMediaDialog,
    onAfterMediaDialog,
    connectionState,
  } = options

  const [isActive, setIsActive] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const sendContextUpdateRef = useRef(sendContextUpdate)
  const streamSessionLockRef = useRef<string | null>(null)

  // Reconnect tracking: was screenshare active when WS disconnected?
  const wasActiveBeforeDisconnectRef = useRef(false)
  const prevConnectionStateRef = useRef<LiveConnectionState | undefined>(connectionState)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const captureLifecycleRef = useRef<CaptureLifecycle | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const metricsRef = useRef<ExtendedCaptureMetrics>(createMetrics())
  const lastAnalysisAtRef = useRef(0)
  const hasSentFirstFrameRef = useRef(false)
  const firstFrameStartedAtRef = useRef<number | null>(null)
  const firstFrameDropWarnedRef = useRef(false)
  const sendRealtimeInputRef = useRef(sendRealtimeInput)
  const sendVideoFrameRef = useRef(sendVideoFrame)
  const captureFrameRef = useRef<(() => Promise<ScreenShareCapture | null>) | null>(null)
  /** Whether a capture frame is currently in-flight; prevents overlapping async captures. */
  const isCapturingRef = useRef(false)
  const hasLiveFrameTransport = Boolean(
    (sendVideoFrame || sendRealtimeInput) && connectionState === LiveConnectionState.CONNECTED
  )
  const ANALYSIS_INTERVAL_MS = 4000
  useEffect(() => {
    if (typeof document !== 'undefined' && !canvasRef.current) {
      canvasRef.current = document.createElement('canvas')
    }
    return () => {
      canvasRef.current = null
    }
  }, [])

  useEffect(() => {
    sendContextUpdateRef.current = sendContextUpdate
  }, [sendContextUpdate])

  useEffect(() => {
    sendRealtimeInputRef.current = sendRealtimeInput
  }, [sendRealtimeInput])

  useEffect(() => {
    sendVideoFrameRef.current = sendVideoFrame
  }, [sendVideoFrame])

  const stopScreenShare = useCallback(() => {
    logger.info('🖥️ [useScreenShare] Stopping screen share')
    hasSentFirstFrameRef.current = false
    firstFrameStartedAtRef.current = null
    firstFrameDropWarnedRef.current = false
    captureLifecycleRef.current?.stop()

    if (streamRef.current) {
      stopScreenShareStream(streamRef.current)
      streamRef.current = null
    }

    setStream(null)
    setIsActive(false)
    setError(null)
    streamSessionLockRef.current = null
  }, [])

  useEffect(() => {
    return registerVisibilityCleanup({
      label: '🖥️ [useScreenShare]',
      getStream: () => streamRef.current,
      clearStream: () => {
        streamRef.current = null
      },
      onStop: stopScreenShare,
    })
  }, [stopScreenShare])

  const startScreenShare = useCallback(async () => {
    if (isInitializing) {
      logger.debug('Screen share initialization already in progress')
      return
    }

    setIsInitializing(true)
    setError(null)
    let mediaTimedOut = false

    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
        const msg = 'Screen sharing is not supported in this browser'
        setError(msg)
        setIsInitializing(false)
        toast.error(msg)
        throw new Error(msg)
      }

      if (!navigator.mediaDevices.getDisplayMedia) {
        const msg = 'Screen sharing API is not available. Please use a modern browser.'
        setError(msg)
        setIsInitializing(false)
        toast.error(msg)
        throw new Error(msg)
      }

      if (!window.isSecureContext) {
        const msg =
          'Screen sharing requires a secure connection (HTTPS). Please access this page via HTTPS.'
        setError(msg)
        setIsInitializing(false)
        toast.error(msg)
        throw new Error(msg)
      }

      if (streamRef.current) {
        stopScreenShareStream(streamRef.current)
        streamRef.current = null
      }

      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      }

      logger.info('🖥️ [useScreenShare] Requesting screen capture with constraints:', constraints)
      // Pause WS heartbeat before browser dialog blocks the main thread
      onBeforeMediaDialog?.()
      let mediaStream: MediaStream
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      const pickerAbort = new AbortController()
      const displayPromise = navigator.mediaDevices.getDisplayMedia({
        ...constraints,
        signal: pickerAbort.signal,
      } as MediaStreamConstraints)
      displayPromise
        .then(lateStream => {
          if (mediaTimedOut && streamRef.current !== lateStream) {
            stopStreamTracks(lateStream)
            logger.warn('🖥️ [useScreenShare] Released screen stream that arrived after timeout')
          }
        })
        .catch(() => undefined)

      try {
        mediaStream = await Promise.race([
          displayPromise,
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => {
                mediaTimedOut = true
                pickerAbort.abort()
                reject(
                  new Error(
                    `Screen share timed out after ${Math.round(MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_START_TIMEOUT_MS / 1000)}s. Please check your browser permissions and try again.`
                  )
                )
              },
              MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_START_TIMEOUT_MS
            )
          }),
        ])
      } finally {
        if (timeoutId) clearTimeout(timeoutId)
        // Always resume heartbeat — whether dialog succeeded, failed, or timed out
        onAfterMediaDialog?.()
      }
      logger.info('🖥️ [useScreenShare] Screen capture permission granted, stream tracks:', {
        count: mediaStream.getTracks().length,
        id: mediaStream.id,
      })

      mediaStream.getTracks().forEach(track => {
        const onTrackEnded = () => {
          if (streamSessionLockRef.current !== mediaStream.id) {
            logger.warn('🖥️ [useScreenShare] Ignoring stale screen share track ended event', {
              endedStreamId: mediaStream.id,
              activeStreamId: streamSessionLockRef.current,
            })
            return
          }
          logger.warn('🖥️ [useScreenShare] Screen share track ended')
          stopScreenShare()
        }
        ;(track as MediaStreamTrack & { _fbcEndedHandler?: () => void })._fbcEndedHandler =
          onTrackEnded
        track.addEventListener('ended', onTrackEnded)
      })

      streamRef.current = mediaStream
      streamSessionLockRef.current = mediaStream.id
      setStream(mediaStream)
      setIsActive(true)
      setIsInitializing(false)
      firstFrameStartedAtRef.current = performance.now()
      logger.info('🖥️ [useScreenShare] Screen share started successfully', {
        streamId: mediaStream.id,
      })

      return mediaStream
    } catch (err) {
      const locale = mediaLocaleFrom(
        typeof document !== 'undefined' ? document.documentElement.lang : undefined,
      )
      const failure = screenShareFailure(err, locale, mediaTimedOut)
      setIsActive(false)
      setIsInitializing(false)
      if (failure.cancelled) {
        setError(null)
        logger.info('🖥️ [useScreenShare] Screen share picker was cancelled')
        return null
      }
      setError(failure.message)
      toast.error(failure.message)
      logger.error('🖥️ [useScreenShare] Screen share startup failed', {
        message: failure.message,
        err,
      })
      return null
    }
  }, [isInitializing, onAfterMediaDialog, onBeforeMediaDialog, stopScreenShare])

  const toggleScreenShare = useCallback(async () => {
    logger.info('🖥️ [useScreenShare] toggleScreenShare called', { isActive, isInitializing })
    if (isInitializing) {
      logger.info('🖥️ [useScreenShare] Initialization in progress, ignoring toggle request.')
      return
    }
    if (isActive) {
      logger.info('🖥️ [useScreenShare] Screen share is active, stopping...')
      stopScreenShare()
    } else {
      logger.info('🖥️ [useScreenShare] Screen share is inactive, starting...')
      await startScreenShare()
    }
  }, [isActive, isInitializing, startScreenShare, stopScreenShare])

  const captureFrame = useCallback(async (): Promise<ScreenShareCapture | null> => {
    // BACKPRESSURE: skip if previous capture still in-flight. This prevents
    // overlapping async captures which cause frame buffer pressure and restart
    // cascades under high load.
    if (isCapturingRef.current) {
      logger.debug('🖥️ [useScreenShare] Capture skipped — previous capture in-flight')
      return null
    }
    isCapturingRef.current = true

    try {
      const canvas = canvasRef.current

      if (!canvas || !streamRef.current) {
        metricsRef.current.failedCaptures++
        return null
      }

      const mediaStream = streamRef.current
      if (!mediaStream) {
        metricsRef.current.failedCaptures++
        return null
      }

      const video = document.createElement('video')
      video.srcObject = mediaStream
      video.muted = true
      video.playsInline = true

      try {
        await video.play()
      } catch (error) {
        logger.warn('🖥️ [useScreenShare] Video play failed before capture', {
          error: error instanceof Error ? error.message : String(error),
        })
      }

      const readyVideo = await waitForVideoReady(() => video, {
        timeoutMs: MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FRAME_READY_TIMEOUT_MS,
      })

      if (!readyVideo) {
        logger.warn('🖥️ [useScreenShare] Video not ready for capture', {
          readyState: video.readyState,
          timeoutMs: MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FRAME_READY_TIMEOUT_MS,
        })
        metricsRef.current.failedCaptures++
        return null
      }

      const startTime = performance.now()

      try {
        // Delegate to shared capture-utils pipeline (same as useCamera)
        const frameResult = await captureVideoFrame(readyVideo, canvas, { maxDimension, quality })

        if (!frameResult) {
          metricsRef.current.failedCaptures++
          return null
        }

        const { blob, imageData, width, height, timestamp } = frameResult

        recordCaptureTime(metricsRef.current, performance.now() - startTime)

        const capture: ScreenShareCapture = {
          blob,
          imageData,
          timestamp,
          metadata: { width, height },
        }

        onCapture?.(blob, imageData)

        if (sendVideoFrameRef.current || sendRealtimeInputRef.current) {
          try {
            const base64Data = await blobToBase64(blob)
            const frame: LiveMediaFrame = {
              mimeType: 'image/jpeg',
              data: base64Data,
            }

            let sentToLive = false
            if (sendVideoFrameRef.current) {
              sentToLive = sendVideoFrameRef.current(frame) !== false
            } else if (sendRealtimeInputRef.current) {
              sendRealtimeInputRef.current([frame])
              sentToLive = true
            }
            if (!sentToLive) {
              const startedAt = firstFrameStartedAtRef.current
              const elapsedMs =
                startedAt !== null ? Math.round(performance.now() - startedAt) : null
              if (
                !hasSentFirstFrameRef.current &&
                !firstFrameDropWarnedRef.current &&
                elapsedMs !== null &&
                elapsedMs >= MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FIRST_FRAME_WARN_MS
              ) {
                firstFrameDropWarnedRef.current = true
                logger.warn('🖥️ [useScreenShare] First Live frame still undelivered past SLO', {
                  elapsedMs,
                  targetMs: MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FIRST_FRAME_TARGET_MS,
                  connectionState,
                })
              } else {
                logger.debug(
                  '🖥️ [useScreenShare] Live transport not ready; retaining frame for fallback analysis',
                  { elapsedMs },
                )
              }
            } else if (!hasSentFirstFrameRef.current) {
              const startedAt = firstFrameStartedAtRef.current
              const elapsedMs =
                startedAt !== null ? Math.round(performance.now() - startedAt) : null
              logger.info('📺 [SCREEN SENT] First frame sent to Live media transport', {
                size: base64Data.length,
                transport: sendVideoFrameRef.current ? 'sendVideoFrame' : 'sendRealtimeInput',
                elapsedMs,
                withinTarget:
                  elapsedMs === null
                    ? null
                    : elapsedMs <= MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FIRST_FRAME_TARGET_MS,
              })
              if (elapsedMs !== null) {
                onFirstFrameSent?.(elapsedMs)
                if (elapsedMs > MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FIRST_FRAME_TARGET_MS) {
                  logger.warn('🖥️ [useScreenShare] First Live frame exceeded target latency', {
                    elapsedMs,
                    targetMs: MEDIA_CAPTURE_CONFIG.SCREEN_SHARE_FIRST_FRAME_TARGET_MS,
                  })
                }
              }
              hasSentFirstFrameRef.current = true
            } else if (sentToLive) {
              logger.debug('📺 [SCREEN SENT] Frame sent to Live media transport', {
                size: base64Data.length,
                transport: sendVideoFrameRef.current ? 'sendVideoFrame' : 'sendRealtimeInput',
              })
            }
          } catch (err) {
            logger.error('❌ Failed to stream screen frame:', err)
          }
        }

        if (sessionId && !hasLiveFrameTransport) {
          await maybeAnalyzeAndBroadcast({
            blob,
            imageData,
            sessionId,
            modality: 'screen',
            voiceConnectionId,
            lastAnalysisAtRef,
            analysisIntervalMs: ANALYSIS_INTERVAL_MS,
            onAnalysis,
            capturedAt: capture.timestamp,
            sendContextUpdate: update => {
              const normalized: VoiceContextUpdate = {
                modality: update.modality,
                analysis: update.analysis,
                ...(update.sessionId !== undefined ? { sessionId: update.sessionId } : {}),
                ...(update.imageData !== undefined ? { imageData: update.imageData } : {}),
                ...(update.capturedAt !== undefined ? { capturedAt: update.capturedAt } : {}),
                ...(update.metadata !== undefined ? { metadata: update.metadata } : {}),
              }
              const previousAnalysis = metricsRef.current.lastAnalysis || undefined
              const delta = analyzeVisualDelta?.(update.analysis, previousAnalysis)
              metricsRef.current.lastAnalysis = update.analysis
              sendContextUpdateRef.current?.({
                ...normalized,
                metadata: {
                  ...(normalized.metadata || {}),
                  ...(delta ? { visualDeltaSignificant: delta.significant, visualDeltaScore: delta.score } : {}),
                },
              })
            },
          })
        }

        return capture
      } catch (error) {
        metricsRef.current.failedCaptures++
        logger.error('🖥️ [useScreenShare] Frame capture failed', { error })
        return null
      }
    } finally {
      isCapturingRef.current = false
    }
  }, [
    maxDimension,
    quality,
    onCapture,
    onFirstFrameSent,
    analyzeVisualDelta,
    onAnalysis,
    sessionId,
    voiceConnectionId,
    hasLiveFrameTransport,
    connectionState,
    ANALYSIS_INTERVAL_MS,
  ])
  useEffect(() => {
    captureFrameRef.current = captureFrame
  }, [captureFrame])

  useEffect(() => {
    const shouldCapture =
      enableAutoCapture && isActive && (!requireVoiceSession || (sessionId && voiceConnectionId))

    if (shouldCapture) {
      if (captureLifecycleRef.current?.isRunning) {
        logger.debug('🖥️ [useScreenShare] Already capturing, skipping setup')
        return
      }

      // Use faster 1fps streaming interval when Live API transport is connected
      // (matches useCamera behavior). Falls back to captureInterval for non-streaming mode.
      const interval = hasLiveFrameTransport ? RATE_LIMITS.SCREEN_STREAM_INTERVAL : captureInterval

      logger.info(
        `🖥️ [useScreenShare] Starting ${hasLiveFrameTransport ? 'streaming' : 'auto-capture'} every ${interval}ms`,
        { hasLiveTransport: hasLiveFrameTransport }
      )

      captureLifecycleRef.current?.dispose()
      captureLifecycleRef.current = new CaptureLifecycle({
        label: '🖥️ [useScreenShare]',
        intervalMs: interval,
        onTick: () => { void captureFrameRef.current?.() },
        logLevel: 'debug',
        fireImmediately: true,
      })
      captureLifecycleRef.current.start()

      return () => {
        captureLifecycleRef.current?.stop()
      }
    }

    if (!shouldCapture) {
      if (captureLifecycleRef.current?.isRunning) {
        captureLifecycleRef.current.stop()
      }
    }
    return undefined
  }, [
    enableAutoCapture,
    isActive,
    captureInterval,
    hasLiveFrameTransport,
    requireVoiceSession,
    sessionId,
    voiceConnectionId,
  ])

  // ── Connection state transition handler for reconnect ──
  useEffect(() => {
    const prev = prevConnectionStateRef.current
    prevConnectionStateRef.current = connectionState

    // No connectionState prop → nothing to track
    if (connectionState === undefined) return

    // Transition: CONNECTED → DISCONNECTED/CONNECTING/ERROR
    // Remember that screenshare was active so we can restart after reconnect
    if (
      prev === LiveConnectionState.CONNECTED &&
      (connectionState === LiveConnectionState.DISCONNECTED ||
        connectionState === LiveConnectionState.CONNECTING ||
        connectionState === LiveConnectionState.ERROR)
    ) {
      if (isActive && streamRef.current) {
        wasActiveBeforeDisconnectRef.current = true
        setIsReconnecting(true)
        setError('Reconnecting screenshare...')
        logger.warn('🖥️ [useScreenShare] WS disconnected — screenshare was active, will auto-resume')
      }
    }

    // Transition: DISCONNECTED/CONNECTING → CONNECTED
    // Auto-restart frame capture if screenshare was active before disconnect
    if (
      connectionState === LiveConnectionState.CONNECTED &&
      (prev === LiveConnectionState.DISCONNECTED ||
        prev === LiveConnectionState.CONNECTING)
    ) {
      if (wasActiveBeforeDisconnectRef.current) {
        wasActiveBeforeDisconnectRef.current = false
        setIsReconnecting(false)
        setError(null)
        logger.info('🖥️ [useScreenShare] WS reconnected — resuming screenshare frame capture')
        toast.success('Screen share reconnected')
        // The stream is still alive (MediaStream tracks persist through WS reconnect).
        // Auto-capture will restart on its own because the effect depends on isActive.
        // Force an immediate capture to reduce gap:
        void captureFrameRef.current?.()
      }
    }

    // Permanent disconnect: only tear down when the browser stream itself is gone.
    // If the stream survived a transport gap, keep the modality active so capture can resume.
    if (
      connectionState === LiveConnectionState.ERROR &&
      wasActiveBeforeDisconnectRef.current &&
      !streamRef.current
    ) {
      wasActiveBeforeDisconnectRef.current = false
      setIsReconnecting(false)
      const msg = 'Voice connection lost permanently. Screen share stopped.'
      setError(msg)
      toast.error(msg)
      logger.error('🖥️ [useScreenShare] Permanent disconnect — cleaning up screenshare', { reason: 'ws_permanent_disconnect' })
      stopScreenShare()
    }
  }, [connectionState, isActive, stopScreenShare])

  useEffect(() => {
    return () => {
      stopScreenShare()
    }
  }, [stopScreenShare])

  const getMetrics = useCallback(() => getMetricsSnapshot(metricsRef.current), [])

  return {
    isActive,
    isInitializing,
    isReconnecting,
    stream,
    error,
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
    captureFrame,
    getMetrics,
  }
}
